import React, { useEffect, useState } from 'react';
import { authFetch, API } from '../api';

// Settings → Announcements (guided P&P phase 6, 2026-10-17; backend/announcements.py). Managers and admins post a
// notice in English; it shows in the bar for everyone until it ends. The ET/RU translation is a paid Claude call per
// notice and language — an admin switches it on here (until then the bar shows English).

interface Ann {
  id: number; source: string; severity: string; text_en: string; audience: string; starts_at: string; ends_at: string;
  created_by: string; removed_at: string | null; live: boolean; translations: Record<string, string>;
}

const card = 'bg-[#1a1d27] rounded-xl border border-gray-800 p-5';
const head = 'text-gray-400 text-xs uppercase tracking-widest font-semibold mb-3';
const input = 'px-2 py-1.5 rounded bg-[#0f1117] border border-gray-700 text-white text-sm';
const when = (s: string) => new Date(s + (s.endsWith('Z') ? '' : 'Z')).toLocaleString();

export default function AnnouncementsSettings() {
  const [rows, setRows] = useState<Ann[]>([]);
  const [settings, setSettings] = useState<{ translate: boolean; languages: string[] } | null>(null);
  const [canSwitch, setCanSwitch] = useState(false);
  const [maxChars, setMaxChars] = useState(300);
  const [text, setText] = useState('');
  const [severity, setSeverity] = useState('warning');
  const [audience, setAudience] = useState('all');
  const [hours, setHours] = useState('24');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = () => authFetch(`${API}/announcements`).then(async r => {
    const d = await r.json().catch(() => ({} as any));
    if (!r.ok) { setErr(d.detail || `HTTP ${r.status}`); return; }
    setRows(d.announcements || []); setSettings(d.settings); setCanSwitch(!!d.can_switch_translation); setMaxChars(d.max_chars || 300);
  }).catch(() => setErr('Could not load'));
  useEffect(() => { load(); }, []);

  const post = async () => {
    setErr(''); setMsg('');
    const ends = new Date(Date.now() + Number(hours || 24) * 3600 * 1000).toISOString();
    const r = await authFetch(`${API}/announcements`, { method: 'POST',
      body: JSON.stringify({ text_en: text, severity, audience, ends_at: ends }) });
    const d = await r.json().catch(() => ({} as any));
    if (!r.ok) { setErr(d.detail || `HTTP ${r.status}`); return; }
    setText(''); setMsg('✓ Posted'); load();
  };
  const remove = async (a: Ann) => {
    if (!window.confirm('Remove this announcement from the bar?')) return;
    const r = await authFetch(`${API}/announcements/${a.id}`, { method: 'DELETE' });
    if (!r.ok) { setErr(`HTTP ${r.status}`); return; }
    load();
  };
  const switchTranslation = async (on: boolean) => {
    if (on && !window.confirm('Translate every notice into the languages below with the Claude API? It is a paid call per notice and language (capped 20 an hour, 60 a day).')) return;
    const r = await authFetch(`${API}/announcements/settings/translation`, { method: 'PUT', body: JSON.stringify({ translate: on }) });
    const d = await r.json().catch(() => ({} as any));
    if (!r.ok) { setErr(d.detail || `HTTP ${r.status}`); return; }
    setSettings(d);
  };

  return (
    <div className="space-y-6">
      <div className={card}>
        <div className={head}>New announcement</div>
        <textarea value={text} onChange={e => setText(e.target.value.slice(0, maxChars))} rows={2}
          placeholder="e.g. HRX goes via Finland today — HRX order by 09:00, Bring parcels ready by 11:00"
          className={`${input} w-full`} />
        <div className="text-xs text-gray-500 mt-1">{text.length}/{maxChars} · written in English; shown to everyone until it ends</div>
        <div className="flex flex-wrap gap-3 items-center mt-3">
          <select value={severity} onChange={e => setSeverity(e.target.value)} className={input}>
            <option value="info">Info (blue)</option><option value="warning">Warning (amber)</option><option value="urgent">Urgent (red)</option>
          </select>
          <select value={audience} onChange={e => setAudience(e.target.value)} className={input}>
            <option value="all">Everyone</option><option value="managers">Managers only</option>
          </select>
          <label className="text-sm text-gray-400">for
            <select value={hours} onChange={e => setHours(e.target.value)} className={`${input} ml-2`}>
              {[['4', '4 hours'], ['8', '8 hours'], ['24', '1 day'], ['72', '3 days'], ['168', '1 week'], ['336', '2 weeks']].map(([v, l]) =>
                <option key={v} value={v}>{l}</option>)}
            </select></label>
          <button onClick={post} disabled={!text.trim()} className="px-4 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-sm disabled:opacity-40">Post</button>
          {msg && <span className="text-sm text-green-400">{msg}</span>}
          {err && <span className="text-sm text-red-400">{err}</span>}
        </div>
      </div>

      {settings && (
        <div className={card}>
          <div className={head}>Translation</div>
          <label className="flex items-center gap-2 text-sm text-gray-200">
            <input type="checkbox" checked={settings.translate} disabled={!canSwitch} onChange={e => switchTranslation(e.target.checked)} />
            Translate notices into {settings.languages.join(', ').toUpperCase() || '—'} (Claude API, paid; the bar shows English until a translation is there)
          </label>
          {!canSwitch && <div className="text-xs text-gray-500 mt-1">Only an admin switches this.</div>}
        </div>
      )}

      <div className={card}>
        <div className={head}>Announcements</div>
        {rows.length === 0 && <div className="text-sm text-gray-500">None yet.</div>}
        <div className="space-y-2">
          {rows.map(a => (
            <div key={a.id} className={`rounded-lg border px-3 py-2 text-sm ${a.live ? 'border-gray-600' : 'border-gray-800 opacity-60'}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`px-1.5 rounded text-xs ${a.severity === 'urgent' ? 'bg-red-700' : a.severity === 'warning' ? 'bg-amber-600 text-black' : 'bg-blue-800'}`}>{a.severity}</span>
                {a.source === 'auto' && <span className="text-xs text-gray-400">automatic</span>}
                {a.audience === 'managers' && <span className="text-xs text-gray-400">managers only</span>}
                <span className="text-gray-500 text-xs">{a.created_by} · until {when(a.ends_at)}{a.removed_at ? ' · removed' : ''}</span>
                <span className="text-gray-500 text-xs">{Object.entries(a.translations || {}).map(([l, s]) => `${l}:${s}`).join(' ')}</span>
                {a.live && <button onClick={() => remove(a)} className="ml-auto px-2 py-0.5 rounded bg-gray-700 hover:bg-gray-600 text-xs">Remove</button>}
              </div>
              <div className="text-gray-100 mt-1">{a.text_en}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
